import { useEffect, useRef } from "react";
import { authedRequest } from "utils/api/requests";
import { installIntercomWidget, isIntercomEnabled } from "utils/thirdParty";

const INTERCOM_APP_ID = "jb774wdc";
// 24h in ms — Intercom's recommended session_duration ceiling.
const INTERCOM_SESSION_DURATION_MS = 86400000;

/**
 * Intercom widget bootstrapper using Messenger Security (JWT-based identity
 * verification).
 *
 * Fetches an HS256-signed JWT from POST /api/v2/users/intercom-jwt and passes
 * it to `Intercom("boot", ...)` as `intercom_user_jwt`. The JWT payload
 * carries the user's email/user_id; passing those alongside the JWT is the
 * impersonation vector that JWT enforcement is designed to close, so they are
 * deliberately omitted from the boot args.
 *
 * The boot effect's cleanup calls `Intercom("shutdown")` (when a boot actually
 * happened) before resetting the boot guard. This covers both unmount (e.g.
 * logout in this SPA — clears the session so a subsequent user on the same tab
 * doesn't inherit the previous conversation history) and an authToken change
 * (Intercom's recommended SPA pattern is to shut down before each new boot).
 *
 * Renders nothing — purely a side-effect host for the Intercom SDK calls.
 */
const Intercom = ({ authToken }) => {
  // Tracked via ref (not state) to avoid an extra render + effect-cleanup
  // cycle when we flip it inside the .then() callback. The deps array stays
  // [authToken] and the boot still fires exactly once per authToken: the
  // cleanup below resets the ref so a subsequent authToken value (e.g. a
  // future token-rotation path) re-runs the effect and re-boots, rather than
  // being silently short-circuited by a stale guard.
  const bootAttemptedRef = useRef(false);

  // Boot effect: fetch JWT, then call Intercom("boot", ...).
  useEffect(() => {
    if (!isIntercomEnabled()) return undefined;
    if (!authToken || bootAttemptedRef.current) return undefined;
    if (typeof window === "undefined") return undefined;

    // This component is the only thing that pulls in the Intercom widget, and
    // it only mounts once an auth token exists — so a logged-out gallery guest
    // never downloads the widget or its frame bundle. The call installs the
    // queue stub synchronously (so the boot below is buffered) and fetches the
    // widget itself after page load.
    installIntercomWidget();

    let cancelled = false;

    authedRequest("v2/users/intercom-jwt", authToken, { method: "POST" })
      .then((response) => {
        if (cancelled) return;
        const jwt = response && response.jwt;
        if (!jwt) {
          // Endpoint responded 200 but without a JWT — treat as fetch failure.
          // eslint-disable-next-line no-console
          console.error("[Intercom] JWT response missing token; not booting.");
          return;
        }
        window.Intercom("boot", {
          app_id: INTERCOM_APP_ID,
          intercom_user_jwt: jwt,
          session_duration: INTERCOM_SESSION_DURATION_MS,
        });
        bootAttemptedRef.current = true;
      })
      .catch((error) => {
        // Silent failure: the widget sits unbooted (and therefore unverified)
        // rather than crashing the host page or exposing identity. Logged for
        // debugging.
        // eslint-disable-next-line no-console
        console.error("[Intercom] JWT fetch failed; not booting:", error);
      });

    return () => {
      cancelled = true;
      // If a boot actually happened, shut the session down before the guard is
      // reset. This cleanup runs on BOTH unmount (logout) and authToken change
      // (the token-rotation path the guard reset unlocks):
      //   - Unmount/logout: clears the SDK session so the next user signing in
      //     on the same tab doesn't inherit the previous conversation history.
      //   - Token rotation: Intercom's recommended SPA pattern is shutdown
      //     before each new boot; without it the next effect run would boot an
      //     already-booted instance, risking duplicate sessions or a stale JWT.
      // Guarded on bootAttemptedRef so an unmount that never booted (e.g. JWT
      // fetch failed or authToken was falsy) doesn't fire a spurious shutdown.
      if (
        bootAttemptedRef.current &&
        typeof window !== "undefined" &&
        window.Intercom
      ) {
        window.Intercom("shutdown");
      }
      // Reset the guard so a new authToken value (the next effect run, after
      // this cleanup) is allowed to boot again. Without this, the ref — scoped
      // to the component instance, not the token — would stay true and skip the
      // re-boot, silently leaving Intercom on a stale JWT after a token change.
      bootAttemptedRef.current = false;
    };
  }, [authToken]);

  return null;
};

export default Intercom;
